/**
 * Snake engine for the hidden arcade (C). Pure game logic: no DOM or MobX.
 */
export const COLS = 24;
export const ROWS = 24;
export const CELL = 8;
export const WIDTH = COLS * CELL;
export const HEIGHT = ROWS * CELL;
export const INITIAL_MOVE_MS = 150;
export const MIN_MOVE_MS = 65;
export const SPEED_STEP_MS = 8;
export const FOODS_PER_LEVEL = 5;
export const FOOD_POINTS = 100;
export const READY_MS = 800;

export const DIRECTIONS = {
    up: { dx: 0, dy: -1, opposite: 'down' },
    down: { dx: 0, dy: 1, opposite: 'up' },
    left: { dx: -1, dy: 0, opposite: 'right' },
    right: { dx: 1, dy: 0, opposite: 'left' },
};

function sameCell(a, b) {
    return a.x === b.x && a.y === b.y;
}

export function moveMsForLevel(level) {
    return Math.max(MIN_MOVE_MS, INITIAL_MOVE_MS - (level - 1) * SPEED_STEP_MS);
}

export function spawnFood(state) {
    const empty = [];
    for (let y = 0; y < ROWS; y += 1) {
        for (let x = 0; x < COLS; x += 1) {
            if (!state.snake.some(cell => cell.x === x && cell.y === y)) {
                empty.push({ x, y });
            }
        }
    }
    if (empty.length === 0) {
        state.food = null;
        return null;
    }
    const rawIndex = Math.floor(state.rng() * empty.length);
    state.food = empty[Math.max(0, Math.min(empty.length - 1, rawIndex))];
    return state.food;
}

export function createGame({ rng = Math.random } = {}) {
    const state = {
        cols: COLS,
        rows: ROWS,
        rng,
        snake: [
            { x: 12, y: 12 },
            { x: 11, y: 12 },
            { x: 10, y: 12 },
            { x: 9, y: 12 },
        ],
        direction: 'right',
        nextDirection: 'right',
        food: null,
        score: 0,
        foods: 0,
        level: 1,
        status: 'ready',
        statusMs: READY_MS,
        moveMs: INITIAL_MOVE_MS,
        moveAccumulatorMs: 0,
        elapsedMs: 0,
        events: [],
    };
    spawnFood(state);
    return state;
}

function endGame(state, completed = false) {
    state.status = 'gameOver';
    state.events.push({ type: 'gameOver', completed });
}

function advance(state) {
    const direction = DIRECTIONS[state.nextDirection];
    state.direction = state.nextDirection;
    const head = state.snake[0];
    const next = { x: head.x + direction.dx, y: head.y + direction.dy };
    const eating = !!state.food && sameCell(next, state.food);
    const bodyLength = state.snake.length - (eating ? 0 : 1);
    const hitBody = state.snake.slice(0, bodyLength).some(cell => sameCell(cell, next));
    const hitWall = next.x < 0 || next.x >= COLS || next.y < 0 || next.y >= ROWS;
    if (hitWall || hitBody) {
        endGame(state);
        return;
    }

    state.snake.unshift(next);
    if (!eating) {
        state.snake.pop();
        return;
    }

    state.foods += 1;
    state.score += FOOD_POINTS * state.level;
    state.events.push({ type: 'eat' });
    const nextLevel = 1 + Math.floor(state.foods / FOODS_PER_LEVEL);
    if (nextLevel > state.level) {
        state.level = nextLevel;
        state.moveMs = moveMsForLevel(state.level);
        state.events.push({ type: 'levelUp', level: state.level });
    }
    if (!spawnFood(state)) {
        endGame(state, true);
    }
}

export function step(state, input = {}, dtMs = 1000 / 60) {
    if (state.status === 'gameOver') {
        return state;
    }
    if (state.status === 'ready') {
        state.statusMs -= dtMs;
        if (state.statusMs <= 0) {
            state.status = 'playing';
            state.statusMs = 0;
            state.events.push({ type: 'start' });
        }
        return state;
    }

    state.elapsedMs += dtMs;
    const requested = input.dir;
    if (DIRECTIONS[requested] && DIRECTIONS[requested].opposite !== state.direction) {
        state.nextDirection = requested;
    }
    state.moveAccumulatorMs += dtMs;
    let moves = 0;
    while (state.moveAccumulatorMs >= state.moveMs && state.status === 'playing' && moves < 4) {
        state.moveAccumulatorMs -= state.moveMs;
        advance(state);
        moves += 1;
    }
    return state;
}
